import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'app-video-section',
  imports: [],
  templateUrl: './video-section.component.html',
  styleUrl: './video-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoSectionComponent implements AfterViewInit, OnDestroy {
  readonly src = input.required<string>();
  readonly isLoading = signal(true);
  readonly videoElement =
    viewChild.required<ElementRef<HTMLVideoElement>>('videoElement');

  private readonly platformId = inject(PLATFORM_ID);
  private intersectionObserver: IntersectionObserver | null = null;

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
    this.checkVideoReadyState();
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  handleVideoLoaded(): void {
    this.isLoading.set(false);
  }

  private checkVideoReadyState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const video = this.videoElement().nativeElement;
    if (video.readyState >= 3) {
      this.handleVideoLoaded();
    }
  }

  private setupIntersectionObserver(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const video = this.videoElement().nativeElement;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.5 },
    );

    this.intersectionObserver.observe(this.videoElement().nativeElement);
  }
}
